import { supabase } from './supabase';
import { syncManager } from './syncManager';
import type { UserStats } from '../types';
import type { Assessment, AssessmentAttempt, AssessmentResult } from '../stores/assessmentStore';

/**
 * Public profile STATS mirror — the stats half of mirrorPublicProfile.
 *
 * The app's real level/XP/domain progress is client-computed (useUserStats
 * over the store blobs) and the real assessment results live in own-only
 * tables, so other users can't read any of it. user_profiles has carried
 * mirror columns for all of it since Bolt (total_points, user_level,
 * total_level, tier_name, assessment_summary) but nothing ever WROTE them —
 * every row sat at 0/'{}'. This module is that missing writer; the Connect
 * profile's Profile tab reads the columns back cross-user.
 *
 * Privacy: the columns are public-readable by design (same as name/avatar);
 * the 'domains'/'assessments' visibility sections are enforced client-side
 * by usePublicProfile, the C1 trust model.
 */

export interface DomainLevelEntry {
  points: number;
  level: number;
  totalLevel: number;
  /** % into the current level, 0-100. */
  progress: number;
}

export interface DomainLevelsPayload {
  v: 1;
  domains: Record<string, DomainLevelEntry>;
}

export interface AssessmentSummaryEntry {
  id: string;
  name: string;
  icon: string;
  color: string;
  type: Assessment['type'];
  completedAt: string | null;
  /** Top dimension names, strongest first (max 3). */
  top: string[];
}

export interface AssessmentSummaryPayload {
  v: 1;
  assessments: AssessmentSummaryEntry[];
}

export function buildDomainLevels(stats: UserStats): DomainLevelsPayload {
  const domains: Record<string, DomainLevelEntry> = {};
  for (const c of stats.categories) {
    domains[c.id] = {
      points: c.points,
      level: c.level,
      totalLevel: c.totalLevel,
      progress: Math.round(c.progress),
    };
  }
  return { v: 1, domains };
}

/**
 * Display-ready summary of the latest completed attempt per assessment.
 * Assessments whose definition or results aren't loaded are skipped — the
 * summary only ever shrinks to what we can render truthfully, and the writer
 * below never replaces a non-empty mirror with an empty one.
 */
export function buildAssessmentSummary(
  assessments: Assessment[],
  attemptHistory: AssessmentAttempt[],
  latestResults: Record<string, AssessmentResult[]>
): AssessmentSummaryPayload {
  const byId = new Map(assessments.map(a => [a.id, a]));

  const latestCompleted = new Map<string, AssessmentAttempt>();
  for (const attempt of attemptHistory) {
    if (attempt.status !== 'completed') continue;
    const current = latestCompleted.get(attempt.assessment_id);
    if (
      !current ||
      new Date(attempt.completed_at || 0).getTime() > new Date(current.completed_at || 0).getTime()
    ) {
      latestCompleted.set(attempt.assessment_id, attempt);
    }
  }

  const entries: AssessmentSummaryEntry[] = [];
  for (const [assessmentId, attempt] of latestCompleted) {
    const definition = byId.get(assessmentId);
    if (!definition) continue;

    const dim = (latestResults[assessmentId] || []).find(r => r.result_type === 'dimension_scores');
    const top = dim?.result_scores
      ? Object.entries(dim.result_scores)
          .sort((a, b) => Number(b[1]) - Number(a[1]))
          .slice(0, 3)
          .map(([name]) => name)
      : [];

    entries.push({
      id: assessmentId,
      name: definition.name,
      icon: definition.icon,
      color: definition.color,
      type: definition.type,
      completedAt: attempt.completed_at,
      top,
    });
  }

  entries.sort((a, b) => new Date(b.completedAt || 0).getTime() - new Date(a.completedAt || 0).getTime());
  return { v: 1, assessments: entries };
}

export interface MirrorInput {
  /** The account these stats were computed for — the only row they may be written to. */
  userId: string;
  stats: UserStats;
  assessments: Assessment[];
  attemptHistory: AssessmentAttempt[];
  latestResults: Record<string, AssessmentResult[]>;
}

const STAMP_KEY = 'metanoia-profile-stats-mirror';
const DEBOUNCE_MS = 4000;

let timer: ReturnType<typeof setTimeout> | null = null;
let pending: MirrorInput | null = null;

/**
 * Debounced, change-skipped mirror write. Stats churn while the stores hydrate
 * on boot — the debounce means only the settled value lands, and the
 * localStorage stamp skips the write entirely on quiet loads (egress/write
 * discipline: zero requests when nothing changed).
 *
 * ⚠️ A sign-out does not reload the page: when the timer fires, another account
 * may be signed in. The snapshot carries the account it was computed for and is
 * written to that account only — never to whoever is signed in by then.
 */
export function queueProfileStatsMirror(input: MirrorInput): void {
  pending = input;
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    const snapshot = pending;
    pending = null;
    if (snapshot) void writeMirror(snapshot);
  }, DEBOUNCE_MS);
}

async function writeMirror(input: MirrorInput): Promise<void> {
  try {
    const { user } = await syncManager.safeAuthCheck();
    if (!user || user.id !== input.userId) return;

    const domainLevels = buildDomainLevels(input.stats);
    const summary = buildAssessmentSummary(
      input.assessments,
      input.attemptHistory,
      input.latestResults
    );

    const update: Record<string, unknown> = {
      total_points: input.stats.totalPoints,
      user_level: input.stats.level,
      total_level: input.stats.totalLevel,
      tier_name: input.stats.tier,
      tier_level: input.stats.tierLevel,
      domain_levels: domainLevels,
      updated_at: new Date().toISOString(),
    };
    // Never replace a possibly-good mirror with an empty summary — this
    // device may simply not have the assessment caches loaded.
    if (summary.assessments.length > 0) {
      update.assessment_summary = summary;
    }

    const stampKey = `${STAMP_KEY}-${user.id}`;
    const { updated_at: _ts, ...hashable } = update;
    const hash = JSON.stringify(hashable);
    try {
      if (localStorage.getItem(stampKey) === hash) return;
    } catch {
      // storage unavailable — fall through and write
    }

    let writtenHash = hash;
    let { error } = await supabase.from('user_profiles').update(update).eq('id', user.id);

    // PGRST204 = domain_levels not migrated yet — mirror the rest meanwhile.
    // Stamp only what actually landed, so the full payload (incl.
    // domain_levels) is retried once the migration runs.
    if (error && (error.code === 'PGRST204' || error.code === '42703')) {
      const { domain_levels: _dl, ...withoutDomains } = update;
      ({ error } = await supabase.from('user_profiles').update(withoutDomains).eq('id', user.id));
      const { updated_at: _ts2, ...hashableFallback } = withoutDomains;
      writtenHash = JSON.stringify(hashableFallback);
    }

    if (error) {
      console.error('Failed to mirror profile stats:', error);
      return;
    }

    try {
      localStorage.setItem(stampKey, writtenHash);
    } catch {
      // ignore — worst case we re-write next load
    }
  } catch (error) {
    console.error('Error mirroring profile stats:', error);
  }
}
